// lib/adminApi.js
import { API_URL } from "./utils";
import { getAdminToken } from "./adminAuth";

/* =========================
   SAFE HELPERS
========================= */
const isProbablyFormData = (body) => {
  if (!body) return false;

  // Standard case
  if (typeof FormData !== "undefined" && body instanceof FormData) return true;

  // Cross-realm / bundler edge cases
  const tag = Object.prototype.toString.call(body); // "[object FormData]"
  if (tag === "[object FormData]") return true;

  // Heuristic: has append/get/entries like FormData
  if (
    typeof body.append === "function" &&
    typeof body.get === "function" &&
    typeof body.entries === "function"
  ) {
    return true;
  }

  return false;
};

const authHeader = () => {
  const token = getAdminToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const jsonHeaders = () => ({
  "Content-Type": "application/json",
  ...authHeader(),
});

/* =========================
   ADMIN FETCH (HARDENED)
   - Detect FormData robustly
   - NEVER set Content-Type for FormData
   - Read response body ONLY ONCE (fixes "body stream already read")
   - Better 422 parsing + readable errors
========================= */
export const adminFetch = async (path, options = {}) => {
  const body = options?.body;
  const isFormData = isProbablyFormData(body);

  // If user passed a plain object body without stringifying, do it safely
  let finalBody = body;
  const shouldJsonify =
    !isFormData &&
    finalBody &&
    typeof finalBody === "object" &&
    !(finalBody instanceof Blob) &&
    !(finalBody instanceof ArrayBuffer);

  if (shouldJsonify) {
    finalBody = JSON.stringify(finalBody);
  }

  const mergedHeaders = {
    ...(isFormData ? authHeader() : jsonHeaders()),
    ...(options.headers || {}),
  };

  // ✅ Safety: never allow manual Content-Type with FormData (boundary issues)
  if (isFormData) {
    delete mergedHeaders["Content-Type"];
    delete mergedHeaders["content-type"];
  }

  const res = await fetch(`${API_URL}${path}`, {
    ...options,
    body: finalBody,
    headers: mergedHeaders,
  });

  // ✅ Read ONCE (prevents "body stream already read" forever)
  const ct = (res.headers.get("content-type") || "").toLowerCase();
  const rawText = await res.text().catch(() => "");
  let data = null;

  const looksJson =
    ct.includes("application/json") ||
    rawText.trim().startsWith("{") ||
    rawText.trim().startsWith("[");

  if (rawText && looksJson) {
    try {
      data = JSON.parse(rawText);
    } catch {
      data = rawText;
    }
  } else {
    data = rawText || null;
  }

  if (!res.ok) {
    // Make FastAPI 422 errors human readable
    let message = `Request failed (${res.status})`;

    // FastAPI typical: { detail: [ {loc: [...], msg: "...", type: "..."} ] }
    if (data && typeof data === "object" && Array.isArray(data.detail)) {
      const parts = data.detail.map((e) => {
        const loc = Array.isArray(e.loc) ? e.loc.join(".") : "field";
        const msg = e.msg || "Invalid value";
        return `${loc}: ${msg}`;
      });
      message = parts.join(" | ") || message;
    } else if (data && typeof data === "object" && typeof data.detail === "string") {
      message = data.detail;
    } else if (data && typeof data === "object" && (data.message || data.error)) {
      message = data.message || data.error;
    } else if (typeof data === "string" && data.trim()) {
      message = data;
    }

    console.error("ADMIN FETCH FAIL:", {
      url: `${API_URL}${path}`,
      status: res.status,
      statusText: res.statusText,
      contentType: ct,
      isFormData,
      data,
      rawText,
    });

    throw new Error(message);
  }

  return data ?? {};
};

/* =========================
   HELPERS
========================= */
const ensureArray = (val) => {
  if (Array.isArray(val)) return val;
  if (typeof val === "string" && val.trim()) return [val.trim()];
  return [];
};

const setCategoriesOnFormData = (fd, categories, primaryFieldName) => {
  const cats = ensureArray(categories).filter(Boolean);

  fd.set("categories_json", JSON.stringify(cats));

  const primary = cats[0] || "";
  if (primaryFieldName) fd.set(primaryFieldName, primary);

  return { cats, primary };
};

/* =========================
   EVENTS (ADMIN)
========================= */
export const listAdminEvents = () => adminFetch("/admin/events");
export const getAdminEvent = (id) => adminFetch(`/admin/events/${id}`);
export const createAdminEvent = (formData) =>
  adminFetch("/admin/events", { method: "POST", body: formData });
export const updateAdminEvent = (id, formData) =>
  adminFetch(`/admin/events/${id}`, { method: "PUT", body: formData });
export const deleteAdminEvent = (id) =>
  adminFetch(`/admin/events/${id}`, { method: "DELETE" });

/* =========================
   PROJECTS (ADMIN)
========================= */
export const listAdminProjects = (isActive) => {
  const qs =
    typeof isActive === "boolean"
      ? `?is_active=${encodeURIComponent(isActive)}`
      : "";
  return adminFetch(`/admin/projects${qs}`);
};

export const createAdminProject = (formDataOrObj) => {
  if (isProbablyFormData(formDataOrObj)) {
    const existingCats = formDataOrObj.get("categories_json");
    if (!existingCats) {
      const focus = String(formDataOrObj.get("focus_area") || "").trim();
      if (focus) setCategoriesOnFormData(formDataOrObj, [focus], "focus_area");
    }
    return adminFetch("/admin/projects", { method: "POST", body: formDataOrObj });
  }

  const obj = formDataOrObj || {};
  const fd = new FormData();
  fd.append("title", (obj.title || "").trim());
  fd.append("excerpt", (obj.excerpt || "").trim());
  fd.append("description", (obj.description || "").trim());
  fd.append("content", (obj.content || "").trim());
  fd.append("is_active", String(!!obj.is_active));
  fd.append("replace_gallery", String(!!obj.replace_gallery));

  setCategoriesOnFormData(fd, obj.categories || [obj.focus_area], "focus_area");

  if (obj.image) fd.append("image", obj.image);
  (obj.gallery_images || []).forEach((f) => fd.append("gallery_images", f));

  return adminFetch("/admin/projects", { method: "POST", body: fd });
};

export const updateAdminProject = (id, formDataOrObj) => {
  if (isProbablyFormData(formDataOrObj)) {
    const existingCats = formDataOrObj.get("categories_json");
    if (!existingCats) {
      const focus = String(formDataOrObj.get("focus_area") || "").trim();
      if (focus) setCategoriesOnFormData(formDataOrObj, [focus], "focus_area");
    }
    return adminFetch(`/admin/projects/${id}`, { method: "PUT", body: formDataOrObj });
  }

  const obj = formDataOrObj || {};
  const fd = new FormData();
  fd.append("title", (obj.title || "").trim());
  fd.append("excerpt", (obj.excerpt || "").trim());
  fd.append("description", (obj.description || "").trim());
  fd.append("content", (obj.content || "").trim());
  fd.append("is_active", String(!!obj.is_active));
  fd.append("replace_gallery", String(!!obj.replace_gallery));

  setCategoriesOnFormData(fd, obj.categories || [obj.focus_area], "focus_area");

  if (obj.image) fd.append("image", obj.image);
  (obj.gallery_images || []).forEach((f) => fd.append("gallery_images", f));

  return adminFetch(`/admin/projects/${id}`, { method: "PUT", body: fd });
};

export const deleteAdminProject = (id) =>
  adminFetch(`/admin/projects/${id}`, { method: "DELETE" });

export const toggleAdminProjectActive = (id) =>
  adminFetch(`/admin/projects/${id}/toggle-active`, { method: "PATCH" });

/* =========================
   BLOG POSTS (ADMIN)
========================= */
export const listAdminPosts = () => adminFetch("/admin/blog/posts");
export const getAdminPost = (id) => adminFetch(`/admin/blog/posts/${id}`);

export const createAdminPost = (formDataOrObj) => {
  if (isProbablyFormData(formDataOrObj)) {
    const existingCats = formDataOrObj.get("categories_json");
    if (!existingCats) {
      const primary = String(formDataOrObj.get("category") || "").trim();
      if (primary) setCategoriesOnFormData(formDataOrObj, [primary], "category");
    }
    return adminFetch("/admin/blog/posts", { method: "POST", body: formDataOrObj });
  }

  const obj = formDataOrObj || {};
  const fd = new FormData();
  fd.append("title", (obj.title || "").trim());
  fd.append("excerpt", (obj.excerpt || "").trim());
  fd.append("content", (obj.content || "<p></p>").trim());
  fd.append("author", (obj.author || "").trim());
  fd.append("image_url", (obj.image_url || "").trim());
  fd.append("is_published", String(!!obj.is_published));
  fd.append("tags_json", JSON.stringify(Array.isArray(obj.tags) ? obj.tags : []));

  setCategoriesOnFormData(fd, obj.categories || [obj.category], "category");

  return adminFetch("/admin/blog/posts", { method: "POST", body: fd });
};

export const updateAdminPost = (id, formDataOrObj) => {
  if (isProbablyFormData(formDataOrObj)) {
    const existingCats = formDataOrObj.get("categories_json");
    if (!existingCats) {
      const primary = String(formDataOrObj.get("category") || "").trim();
      if (primary) setCategoriesOnFormData(formDataOrObj, [primary], "category");
    }
    return adminFetch(`/admin/blog/posts/${id}`, { method: "PUT", body: formDataOrObj });
  }

  const obj = formDataOrObj || {};
  const fd = new FormData();
  fd.append("title", (obj.title || "").trim());
  fd.append("excerpt", (obj.excerpt || "").trim());
  fd.append("content", (obj.content || "<p></p>").trim());
  fd.append("author", (obj.author || "").trim());
  fd.append("image_url", (obj.image_url || "").trim());
  fd.append("is_published", String(!!obj.is_published));
  fd.append("tags_json", JSON.stringify(Array.isArray(obj.tags) ? obj.tags : []));

  setCategoriesOnFormData(fd, obj.categories || [obj.category], "category");

  return adminFetch(`/admin/blog/posts/${id}`, { method: "PUT", body: fd });
};

export const deleteAdminPost = (id) =>
  adminFetch(`/admin/blog/posts/${id}`, { method: "DELETE" });

/* =========================
   BLOG COMMENTS (ADMIN)
========================= */
export const listAdminComments = (status = "pending") =>
  adminFetch(`/admin/blog/comments?status=${encodeURIComponent(status)}`);

export const approveComment = (id) =>
  adminFetch(`/admin/blog/comments/${id}/approve`, { method: "PATCH" });

export const rejectComment = (id) =>
  adminFetch(`/admin/blog/comments/${id}/reject`, { method: "PATCH" });

export const deleteComment = (id) =>
  adminFetch(`/admin/blog/comments/${id}`, { method: "DELETE" });

/* =========================
   BLOG IMAGE UPLOAD (ADMIN)
========================= */
export const uploadBlogImage = async (file) => {
  const fd = new FormData();
  fd.append("image", file);
  return adminFetch("/admin/blog/upload-image", { method: "POST", body: fd });
};

/* =========================
   CONTACT + GET INVOLVED (ADMIN)
========================= */
export const listAdminContacts = (params = {}) => {
  const q = new URLSearchParams();
  if (params.limit) q.set("limit", String(params.limit));
  if (params.skip) q.set("skip", String(params.skip));
  return adminFetch(`/admin/contacts${q.toString() ? `?${q.toString()}` : ""}`);
};

export const listAdminVolunteers = (params = {}) => {
  const q = new URLSearchParams();
  if (params.limit) q.set("limit", String(params.limit));
  if (params.skip) q.set("skip", String(params.skip));
  return adminFetch(`/admin/volunteers${q.toString() ? `?${q.toString()}` : ""}`);
};

export const listAdminPartners = (params = {}) => {
  const q = new URLSearchParams();
  if (params.limit) q.set("limit", String(params.limit));
  if (params.skip) q.set("skip", String(params.skip));
  return adminFetch(`/admin/partners${q.toString() ? `?${q.toString()}` : ""}`);
};

export const listAdminDonations = (params = {}) => {
  const q = new URLSearchParams();
  if (params.limit) q.set("limit", String(params.limit));
  if (params.skip) q.set("skip", String(params.skip));
  return adminFetch(`/admin/donations${q.toString() ? `?${q.toString()}` : ""}`);
};

export const listAdminNewsletters = (params = {}) => {
  const q = new URLSearchParams();
  if (params.limit) q.set("limit", String(params.limit));
  if (params.skip) q.set("skip", String(params.skip));
  return adminFetch(`/admin/newsletters${q.toString() ? `?${q.toString()}` : ""}`);
};

/* =========================
   EXPORT URL HELPERS (CSV)
========================= */
export const adminExportUrls = {
  contactsCsv: () => `${API_URL}/admin/contacts/export.csv`,
  volunteersCsv: () => `${API_URL}/admin/volunteers/export.csv`,
  partnersCsv: () => `${API_URL}/admin/partners/export.csv`,
  donationsCsv: () => `${API_URL}/admin/donations/export.csv`,
  newslettersCsv: () => `${API_URL}/admin/newsletters/export.csv`,
};

export const fetchAdminCsv = async (path) => {
  const res = await fetch(`${API_URL}${path}`, {
    headers: {
      ...authHeader(),
    },
  });

  if (!res.ok) {
    const txt = await res.text().catch(() => "");
    throw new Error(txt || "Failed to export CSV");
  }

  return res.blob();
};

/* =========================
   TATL1000 STORIES (ADMIN)
========================= */
export const listAdminTatlStories = (isPublished) => {
  const qs =
    typeof isPublished === "boolean"
      ? `?is_published=${encodeURIComponent(isPublished)}`
      : "";
  return adminFetch(`/admin/tatl/stories${qs}`);
};

export const getAdminTatlStory = (id) => adminFetch(`/admin/tatl/stories/${id}`);

export const createAdminTatlStory = (formData) =>
  adminFetch("/admin/tatl/stories", { method: "POST", body: formData });

export const updateAdminTatlStory = (id, formData) =>
  adminFetch(`/admin/tatl/stories/${id}`, { method: "PUT", body: formData });

export const deleteAdminTatlStory = (id) =>
  adminFetch(`/admin/tatl/stories/${id}`, { method: "DELETE" });
// =========================
// ADMIN GALLERY API (Aligned to gallery_backend.py)
// =========================

// List folders
export async function listAdminGalleryFolders(limit = 2000) {
  return adminFetch(`/admin/gallery/folders?limit=${encodeURIComponent(limit)}`);
}

// Create folder (FormData)
export async function createAdminGalleryFolder(payload) {
  const fd = new FormData();
  fd.append("name", String(payload?.name || "").trim());
  fd.append("description", String(payload?.description || "").trim());
  if (String(payload?.project_slug || "").trim()) {
    fd.append("project_slug", String(payload.project_slug).trim());
  }

  return adminFetch("/admin/gallery/folders", {
    method: "POST",
    body: fd,
    isMultipart: true,
  });
}

// Update folder (FormData)
export async function updateAdminGalleryFolder(folderId, payload) {
  const fd = new FormData();
  fd.append("name", String(payload?.name || "").trim());
  fd.append("description", String(payload?.description || "").trim());
  if (String(payload?.project_slug || "").trim()) {
    fd.append("project_slug", String(payload.project_slug).trim());
  } else {
    // allow clearing
    fd.append("project_slug", "");
  }

  return adminFetch(`/admin/gallery/folders/${encodeURIComponent(folderId)}`, {
    method: "PUT",
    body: fd,
    isMultipart: true,
  });
}

// Delete folder
export async function deleteAdminGalleryFolder(folderId) {
  return adminFetch(`/admin/gallery/folders/${encodeURIComponent(folderId)}`, {
    method: "DELETE",
  });
}

// Folder details by slug
export async function getAdminGalleryFolderBySlug(slug) {
  return adminFetch(`/admin/gallery/folders/by-slug/${encodeURIComponent(slug)}`);
}

// Add images (multipart) key must be "images" repeated
export async function addAdminGalleryImages(folderId, files = [], caption = "") {
  const fd = new FormData();
  fd.append("caption", String(caption || "").trim());
  (files || []).forEach((f) => {
    fd.append("images", f); // ✅ MUST be "images"
  });

  return adminFetch(`/admin/gallery/folders/${encodeURIComponent(folderId)}/images`, {
    method: "POST",
    body: fd,
    isMultipart: true,
  });
}

// Add YouTube (FormData)
export async function addAdminGalleryYoutube(folderId, url, caption = "") {
  const fd = new FormData();
  fd.append("url", String(url || "").trim()); // ✅ backend expects Form(...)
  fd.append("caption", String(caption || "").trim());

  return adminFetch(`/admin/gallery/folders/${encodeURIComponent(folderId)}/youtube`, {
    method: "POST",
    body: fd,
    isMultipart: true,
  });
}

// Delete item (image or youtube)
export async function deleteAdminGalleryItem(itemId) {
  return adminFetch(`/admin/gallery/items/${encodeURIComponent(itemId)}`, {
    method: "DELETE",
  });
}